import { Button, toast } from '@folio/components';
import { type QueryObserverResult, useQuery } from '@tanstack/react-query';
import {
  getClaimsReferenceDate,
  isHistoricalClaim as isClaimHistorical,
} from '@folio/utils/claims';
import {
  type ClaimDto,
  type Metadata,
  type MissingTenantErrorDto,
  type ResponseErrorConfig,
  keyValueFieldsToObject,
  policyControllerGetPolicyQueryOptions,
  toKeyValueFields,
  useClaimsControllerCreate,
} from 'korint-sdk';
import { PlusIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { PolicyClaimItem } from './PolicyClaimItem';
import { Title } from '@folio/components/ui/text';

interface PolicyClaimsCategoryProps {
  category: string;
  label: string;
  categoryClaims: ClaimDto[];
  hasResponsibility?: boolean;
  claimsMetadata?: Record<string, string>;
  fromDate?: Date;
  refetchClaims: () => Promise<
    QueryObserverResult<ClaimDto[], ResponseErrorConfig<MissingTenantErrorDto>>
  >;
  mode?: 'new-business' | 'add-only';
}

// @debt: this module is becoming overly complicated with a lot of
// logic to derive add/edit/delete permissions from policy status, branch state
// and metadata-tagged claim ownership. We should probably have a backend
// endpoint that tells us if we can add/edit/delete a given claim.
export function PolicyClaimsCategory({
  category,
  label,
  hasResponsibility,
  claimsMetadata,
  categoryClaims,
  fromDate,
  refetchClaims,
  mode = 'new-business',
}: PolicyClaimsCategoryProps) {
  const { t } = useTranslation();
  const { policyId, branchId } = useParams();

  const { data: policyOnBase, isLoading: isPolicyOnBaseLoading } = useQuery({
    ...policyControllerGetPolicyQueryOptions(policyId!),
    enabled: !!policyId,
    select: data => ({
      ...data,
      customFields: keyValueFieldsToObject(data.customFields ?? []),
    }),
  });

  const { data: policyOnBranch, isLoading: isPolicyOnBranchLoading } = useQuery({
    ...policyControllerGetPolicyQueryOptions(policyId!, { branchId }),
    enabled: !!policyId,
    select: data => ({
      ...data,
      customFields: keyValueFieldsToObject(data.customFields ?? []),
    }),
  });

  const { mutate: createClaim, isPending: isCreateClaimLoading } = useClaimsControllerCreate({
    mutation: {
      onSuccess: () => refetchClaims(),
      onError: () => {
        toast({
          description: t('policyClaims.updateError'),
        });
      },
    },
  });

  const toDate = getClaimsReferenceDate(policyOnBranch?.startedAt);

  const isPolicyInNewBusiness = policyOnBase?.status === 'POLICY_CREATED';

  const isViewLoading = isPolicyOnBaseLoading || isPolicyOnBranchLoading;

  const isAddOnlyMode = mode === 'add-only';

  const canAddClaim = isAddOnlyMode ? !isViewLoading : isPolicyInNewBusiness && !isViewLoading;

  const isViewReadonly = isViewLoading || !isPolicyInNewBusiness;

  const claimCreationMetadata =
    isAddOnlyMode && branchId
      ? { ...(claimsMetadata ?? {}), createdOnBranch: branchId }
      : claimsMetadata;

  return (
    <>
      <Title
        order={3}
        data-name={category}
        className="text-md flex items-center justify-between py-5 font-medium"
      >
        {label}

        {canAddClaim && (
          <Button
            type="button"
            variant="ghost"
            size="xs"
            onClick={() =>
              createClaim({
                data: {
                  category,
                  externalId: policyId!,
                  metadata:
                    claimCreationMetadata &&
                    (toKeyValueFields(claimCreationMetadata) as Metadata[]),
                  createdAt: new Date().toISOString(),
                  occurredAt: toDate.toISOString(),
                },
              })
            }
            disabled={isCreateClaimLoading}
            loading={isCreateClaimLoading}
            leftIcon={<PlusIcon className="h-4" />}
          >
            {t('policyClaims.addClaim')}
          </Button>
        )}
      </Title>
      <div>
        {categoryClaims?.length ? (
          <div className="divide-y border rounded-md">
            {categoryClaims?.map((claim, i) => {
              const isHistoricalClaim = isClaimHistorical(claim, toDate);

              const claimMetadata = keyValueFieldsToObject(claim.metadata ?? []);
              const wasCreatedOnThisBranch =
                !!branchId && claimMetadata.createdOnBranch === branchId;

              const isClaimReadonly = isAddOnlyMode
                ? !wasCreatedOnThisBranch
                : isViewReadonly ||
                  !isHistoricalClaim ||
                  Boolean(isHistoricalClaim && !isPolicyInNewBusiness);

              return (
                <PolicyClaimItem
                  key={claim.id}
                  claim={claim}
                  title={t('policyClaims.itemTitle', { n: i + 1 })}
                  hasResponsibility={hasResponsibility}
                  fromDate={fromDate}
                  toDate={toDate.toDate()}
                  isHistoricalClaim={isHistoricalClaim}
                  isReadonly={isClaimReadonly}
                  refetchClaims={refetchClaims}
                />
              );
            })}
          </div>
        ) : null}
      </div>
    </>
  );
}
